import { BasePageDto } from "@/types/dto/base-page-dto";
import { IdDto } from "@/types/dto/id-dto";
import { QuoteDetailDto } from "@/types/dto/quote-detail-dto";
import { CreateQuotePayload } from "@/types/payload/create-quote-payload";
import { QuotePagePayload } from "@/types/payload/quote-page-payload";
import { QuotePreviewPayload } from "@/types/payload/quote-preview-payload";
import { AxiosInstance } from "axios";
import { BaseResponse } from "../types/dto/base-response";
import { baseApi } from "./base-api";

enum QuoteServiceEndpoint {
  Base = "/quotation",
  Create = "/quotation",
  GetById = "/quotation",
  GetPage = "/quotations",
  Preview = "/quotation/preview",
  Validate = "/validate",
}

class QuoteService {
  private static instance: QuoteService;
  private _api: AxiosInstance;

  private constructor(api: AxiosInstance) {
    this._api = api;
  }

  static getInstance(apiClient = baseApi): QuoteService {
    if (!QuoteService.instance) {
      QuoteService.instance = new QuoteService(apiClient);
    }
    return QuoteService.instance;
  }

  async create(
    payload: CreateQuotePayload
  ): Promise<BaseResponse<IdDto, CreateQuotePayload>> {
    try {
      const response = await this._api.post<
        BaseResponse<IdDto, CreateQuotePayload>
      >(QuoteServiceEndpoint.Create, payload);
      return response.data;
    } catch (error) {
      console.error("Error in createQuote:", error);
      throw error;
    }
  }

  async getById(id: number): Promise<BaseResponse<QuoteDetailDto, void>> {
    try {
      const response = await this._api.get<BaseResponse<QuoteDetailDto, void>>(
        `${QuoteServiceEndpoint.GetById}/${id}`
      );
      return response.data;
    } catch (error) {
      console.error("Error in getById:", error);
      throw error;
    }
  }

  async getPage(
    payload: QuotePagePayload
  ): Promise<BaseResponse<BasePageDto<QuoteDetailDto>, QuotePagePayload>> {
    try {
      const queryParams = new URLSearchParams();

      queryParams.append("page", payload.page.toString());
      queryParams.append("size", payload.size.toString());
      if (payload.fromDate) {
        queryParams.append("fromDate", payload.fromDate.toISOString());
      }
      if (payload.toDate) {
        queryParams.append("toDate", payload.toDate.toISOString());
      }
      if (payload.orderType) {
        queryParams.append("orderType", payload.orderType.toString());
      }
      if (payload.partnerName && payload.partnerName !== "") {
        queryParams.append("partnerName", payload.partnerName);
      }

      const response = await this._api.get<
        BaseResponse<BasePageDto<QuoteDetailDto>, QuotePagePayload>
      >(`${QuoteServiceEndpoint.GetPage}?${queryParams.toString()}`);

      return response.data;
    } catch (error) {
      console.error("Error in getPage:", error);
      throw error;
    }
  }

  async getPagePartner(
    id: number,
    payload: QuotePagePayload
  ): Promise<BaseResponse<BasePageDto<QuoteDetailDto>, QuotePagePayload>> {
    try {
      const queryParams = new URLSearchParams();

      queryParams.append("page", payload.page.toString());
      queryParams.append("size", payload.size.toString());
      if (payload.fromDate) {
        queryParams.append("fromDate", payload.fromDate.toISOString());
      }
      if (payload.toDate) {
        queryParams.append("toDate", payload.toDate.toISOString());
      }
      if (payload.orderType) {
        queryParams.append("orderType", payload.orderType.toString());
      }
      if (payload.partnerName && payload.partnerName !== "") {
        queryParams.append("partnerName", payload.partnerName);
      }

      const response = await this._api.get<
        BaseResponse<BasePageDto<QuoteDetailDto>, QuotePagePayload>
      >(`${QuoteServiceEndpoint.GetPage}/${id}?${queryParams.toString()}`);

      return response.data;
    } catch (error) {
      console.error("Error in getPage:", error);
      throw error;
    }
  }

  async preview(
    payload: QuotePreviewPayload[]
  ): Promise<BaseResponse<QuoteDetailDto, QuotePreviewPayload[]>> {
    try {
      const response = await this._api.post<
        BaseResponse<QuoteDetailDto, QuotePreviewPayload[]>
      >(QuoteServiceEndpoint.Preview, payload);
      return response.data;
    } catch (error) {
      console.error("Error in preview:", error);
      throw error;
    }
  }

  async validate(id: number): Promise<BaseResponse<void, void>> {
    try {
      const response = await this._api.get<BaseResponse<void, void>>(
        QuoteServiceEndpoint.Base + `/${id}` + QuoteServiceEndpoint.Validate
      );
      return response.data;
    } catch (error) {
      console.error("Error in validate:", error);
      throw error;
    }
  }
}

export const quoteService = QuoteService.getInstance(baseApi);
